import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { subscribeSharedFirestore } from './sharedFirestoreSubscription';

const taskCollection = collection(firestore, 'tasks');

export function subscribeAssignments({ role, userId, onData, onError }) {
  const normalizedRole = String(role || '').toLowerCase();
  const isStaff = normalizedRole === 'staff';
  const taskQuery = isStaff
    ? query(taskCollection, where('assignedToId', '==', userId))
    : query(taskCollection);
  const key = isStaff ? `tasks:staff:${userId}` : 'tasks:all';

  return subscribeSharedFirestore({
    key,
    onData,
    onError,
    start: (emit, emitError) => onSnapshot(
      taskQuery,
      (snapshot) => emit(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
      emitError
    ),
  });
}

export function subscribeClients(onData, onError) {
  return subscribeSharedFirestore({
    key: 'clients:all',
    onData,
    onError,
    start: (emit, emitError) => onSnapshot(
      collection(firestore, 'clients'),
      (snapshot) => emit(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
      emitError
    ),
  });
}

export function subscribeUsers(onData, onError) {
  return subscribeSharedFirestore({
    key: 'users:all',
    onData,
    onError,
    start: (emit, emitError) => onSnapshot(
      collection(firestore, 'users'),
      (snapshot) => emit(snapshot.docs.map((item) => ({ id: item.id, ...item.data() }))),
      emitError
    ),
  });
}

function dateOrNull(value) {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
}

function basePayload(values, clients, users) {
  const client = clients.find((item) => item.id === values.clientId);
  const assignee = users.find((item) => item.id === values.assignedToId);
  const assignmentType = values.assignmentType.trim();

  return {
    clientId: values.clientId,
    clientName: client?.name || '',
    assignmentType,
    category: assignmentType,
    financialYear: values.financialYear?.trim() || '',
    period: values.period?.trim() || '',
    assignedToId: values.assignedToId,
    assignedToName: assignee?.name || assignee?.username || assignee?.email || '',
    startDate: dateOrNull(values.startDate),
    dueDate: dateOrNull(values.dueDate),
    remarks: values.remarks?.trim() || '',
    status: values.status,
  };
}

export async function createAssignment(values, clients, users, creator) {
  const payload = basePayload(values, clients, users);
  return addDoc(taskCollection, {
    ...payload,
    billingStatus: 'Not Sent',
    billingCompleted: false,
    isArchived: false,
    createdById: creator?.id || '',
    createdByName: creator?.name || creator?.username || creator?.email || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateAssignment(id, values, clients, users) {
  return updateDoc(doc(firestore, 'tasks', id), {
    ...basePayload(values, clients, users),
    updatedAt: serverTimestamp(),
  });
}

export async function linkAssignmentToMatter(id, matterId, matterCode = '') {
  return updateDoc(doc(firestore, 'tasks', id), {
    matterId,
    matterCode,
    updatedAt: serverTimestamp(),
  });
}

export async function updateOwnAssignmentProgress(id, { status, remarks }) {
  return updateDoc(doc(firestore, 'tasks', id), {
    status,
    remarks: remarks?.trim() || '',
    updatedAt: serverTimestamp(),
  });
}

export async function archiveAssignment(id) {
  return updateDoc(doc(firestore, 'tasks', id), {
    isArchived: true,
    archivedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
